// useBoardCardDnD.js

import { useCallback, useState } from 'react';

import { useUser } from 'context/UserContext';
import { toast } from 'react-toastify';

import { updateNode } from 'services/nodes/NodeWriteService';
import { updateView } from 'services/views/ViewWriteService';
import { buildBoardOrderPayload } from 'views/board/utils/boardOrder';
import { normalizeNodelistValue } from 'utilities/NodelistField';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';
import { canPersistViewLayout } from 'views/utils/organicViews';

const useBoardCardDnD = ({
    cancelPendingActionsOpen,
    optimisticBoardOrder,
    setOptimisticBoardOrder,
    optimisticFieldValues,
    setOptimisticFieldValues,
    boardFieldKey,
    boardData,
    otherKey,
    unassignedKey,
    nodes,
    actionNode,
    boardFieldDef,
    boardFieldStorageKey,
    normalizedBoardOrder,
    viewKey,
    persistBoardOrder = true,
    showStatusSaveToast = false,
    typeDef = null,
}) => {
    const { user } = useUser();
    const [draggingNodeKey, setDraggingNodeKey] = useState('');
    const [draggingSourceColumnKey, setDraggingSourceColumnKey] = useState('');
    const [dragOverTarget, setDragOverTarget] = useState(null);

    const resetDragState = useCallback(() => {
        setDraggingNodeKey('');
        setDraggingSourceColumnKey('');
        setDragOverTarget(null);
    }, []);

    const handleCardDragStart = useCallback((node, columnKey, event) => {
        const nodeKey = typeof node?.key === 'string' ? node.key : '';
        if (!nodeKey || !columnKey) return;
        cancelPendingActionsOpen();

        event.stopPropagation();
        // Node drag payload (alongside board move text/plain).
        writeNodeDragData(event, node);
        event.dataTransfer.effectAllowed = 'copyMove';
        event.dataTransfer.setData('text/plain', nodeKey);
        setDraggingNodeKey(nodeKey);
        setDraggingSourceColumnKey(columnKey);
    }, [cancelPendingActionsOpen]);

    const handleCardDragEnd = useCallback(() => {
        resetDragState();
    }, [resetDragState]);

    const handleInsertDragOver = useCallback((columnKey, insertIndex, event) => {
        if (!draggingNodeKey || !columnKey || columnKey === otherKey) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        const isSameTarget = dragOverTarget
            && dragOverTarget.columnKey === columnKey
            && dragOverTarget.insertIndex === insertIndex;

        if (!isSameTarget) {
            setDragOverTarget({ columnKey, insertIndex });
        }
    }, [dragOverTarget, draggingNodeKey, otherKey]);

    const handleInsertDrop = useCallback(async (columnKey, insertIndex, event) => {
        if (!draggingNodeKey || !draggingSourceColumnKey || !columnKey || columnKey === otherKey) return;
        event.preventDefault();

        const shouldUpdateField = draggingSourceColumnKey !== columnKey;
        const previousBoardOrder = optimisticBoardOrder;
        const previousFieldValues = optimisticFieldValues;
        const nextBoardOrder = buildBoardOrderPayload({
            boardFieldKey,
            boardData,
            nodeKey: draggingNodeKey,
            sourceColumnKey: draggingSourceColumnKey,
            targetColumnKey: columnKey,
            targetInsertIndex: insertIndex,
            excludedColumnKeys: [otherKey],
        });

        const isNodelistBoard = isRelationFieldType(boardFieldDef);
        const nextFieldValue = columnKey === unassignedKey
            ? (isNodelistBoard ? [] : null)
            : (isNodelistBoard
                ? normalizeNodelistValue([columnKey], { allowMultiple: false })
                : columnKey);
        const nextFieldOverrides = shouldUpdateField
            ? {
                ...previousFieldValues,
                [draggingNodeKey]: nextFieldValue,
            }
            : previousFieldValues;

        setOptimisticBoardOrder(nextBoardOrder);
        setOptimisticFieldValues(nextFieldOverrides);
        resetDragState();

        const movedNode = nodes.find((node) => node?.key === draggingNodeKey) || actionNode;
        const fieldName = boardFieldDef?.name || boardFieldDef?.key || boardFieldKey;
        const currentSectionData = boardFieldStorageKey
            && movedNode?.[boardFieldStorageKey]
            && typeof movedNode[boardFieldStorageKey] === 'object'
            ? movedNode[boardFieldStorageKey]
            : {};

        if (shouldUpdateField && !boardFieldStorageKey) {
            setOptimisticBoardOrder(previousBoardOrder);
            setOptimisticFieldValues(previousFieldValues);
            toast.error('Could not move card: board field storage section is unavailable.');
            return;
        }

        try {
            if (shouldUpdateField) {
                await updateNode(draggingNodeKey, {
                    [boardFieldStorageKey]: {
                        ...currentSectionData,
                        [fieldName]: nextFieldValue,
                    },
                }, { actor: user });

                if (showStatusSaveToast) {
                    toast.success('Status updated');
                }
            }
        } catch (error) {
            setOptimisticBoardOrder(previousBoardOrder);
            setOptimisticFieldValues(previousFieldValues);
            toast.error(`Could not move card: ${error?.message || 'Unknown error'}`);
            return;
        }

        if (!persistBoardOrder || !viewKey || !canPersistViewLayout({ key: viewKey })) {
            return;
        }

        try {
            await updateView(viewKey, { boardOrder: nextBoardOrder });
        } catch (error) {
            setOptimisticBoardOrder(normalizedBoardOrder);
            toast.error(`Card moved, but order could not be saved: ${error?.message || 'Unknown error'}`);
        }
    }, [
        actionNode,
        boardData,
        boardFieldDef?.key,
        boardFieldDef?.name,
        boardFieldDef?.type,
        boardFieldKey,
        boardFieldStorageKey,
        draggingNodeKey,
        draggingSourceColumnKey,
        nodes,
        normalizedBoardOrder,
        optimisticBoardOrder,
        optimisticFieldValues,
        otherKey,
        resetDragState,
        setOptimisticBoardOrder,
        setOptimisticFieldValues,
        unassignedKey,
        user,
        viewKey,
        persistBoardOrder,
        showStatusSaveToast,
        typeDef,
    ]);

    return {
        draggingNodeKey,
        dragOverTarget,
        handleCardDragStart,
        handleCardDragEnd,
        handleInsertDragOver,
        handleInsertDrop,
    };
};

export default useBoardCardDnD;
